import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { i18n } from "@/i18n";
import envConfig from "@/helpers/envConfig.ts";
import { requestRaw } from "@/http/raw";

const API_AUTH_URL = envConfig.VUE_APP_API_URL;

// TODO: fix User type
type User = Record<string, unknown>;

export const useAuthStore = defineStore("auth", () => {
  // state
  const access_token = ref(localStorage.getItem("access_token"));
  const access_token_expire_at = ref(
    Number(localStorage.getItem("access_token_expire_at")) as number | null
  );
  const refresh_token = ref(localStorage.getItem("refresh_token"));
  const refresh_token_expire_at = ref(
    Number(localStorage.getItem("refresh_token_expire_at")) as number | null
  );
  const localStorageUser = localStorage.getItem("user");
  const user = ref(
    typeof localStorageUser === "string" ? JSON.parse(localStorageUser) : null
  );
  const userApiErrorMessage = ref(null as string | null);
  const userMessage = ref(null as string | null);
  const userRequest = ref(false);

  const now = Math.ceil(Date.now() / 1000);

  // getters
  const accessIsExpired = computed(
    () => (access_token_expire_at.value ?? 0) - now <= 0
  );
  const refreshIsExpired = computed(
    () => (refresh_token_expire_at.value ?? 0) - now <= 0
  );
  const isAuthorised = computed(
    () => access_token.value && (access_token_expire_at.value ?? 0) - now > 0
  );
  const userName = computed(
    () => user.value && user.value.surname + " " + user.value.name
  );
  const userRole = computed(() => user?.value?.roles[0]);
  const userDepartment = computed(() => user.value?.department);

  const refreshTimeout = ref(null as ReturnType<typeof setTimeout> | null);

  function initStore() {
    userRequest.value = false;
    userApiErrorMessage.value = null;
    return initRefreshTimer();
  }

  // actions
  function updateTokenData(data: {
    access_token?: string;
    refresh_token?: string;
    expires_in?: number;
    refresh_expires_in?: number;
    user?: User;
    config?: unknown;
  }) {
    if (data.access_token) {
      localStorage.setItem("access_token", data.access_token);
      access_token.value = data.access_token;
    }

    if (data.refresh_token) {
      localStorage.setItem("refresh_token", data.refresh_token);
      refresh_token.value = data.refresh_token;
    }

    if (data.expires_in) {
      access_token_expire_at.value =
        Math.ceil(Date.now() / 1000) + data.expires_in - 10;
      localStorage.setItem(
        "access_token_expire_at",
        String(access_token_expire_at.value)
      );
    }

    if (data.refresh_expires_in) {
      refresh_token_expire_at.value =
        Math.ceil(Date.now() / 1000) + data.refresh_expires_in - 10;
      localStorage.setItem(
        "refresh_token_expire_at",
        String(refresh_token_expire_at.value)
      );
    }

    if (data.user) {
      user.value = data.user;
      localStorage.setItem("user", JSON.stringify(data.user));
    }

    if (data.config) {
      localStorage.setItem("config", JSON.stringify(data.config));
    }

    initRefreshTimer();
  }

  /**
   * Check access and refresh expire timers to detect time when need to get a fresh token.
   * When access token time is expired
   */
  function initRefreshTimer() {
    if (refreshTimeout.value) {
      clearTimeout(refreshTimeout.value);
      refreshTimeout.value = null;
    }
    const now = Math.ceil(Date.now() / 1000);
    let timer = (access_token_expire_at.value ?? 0) - now;

    if (timer <= 0) {
      const refreshDiff = (refresh_token_expire_at.value ?? 0) - now;
      if (refreshDiff > 0) {
        // refresh now
        return refreshUserToken();
      } else if (refreshDiff < 0) {
        // access data too old
        clearLocalStorageData();
      }
    }
    if (timer >= 0) {
      if (timer > 30) {
        timer -= 30;
      }
      refreshTimeout.value = setTimeout(refreshUserToken, timer * 1000);
    }
    return Promise.resolve(true);
  }

  async function refreshUserToken() {
    try {
      const res = await requestRaw("POST", `${API_AUTH_URL}/api/auth/refresh`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${access_token.value}`,
        },
      });
      if (res.status === 200) {
        const data = await res.json();
        updateTokenData(data);
        return true;
      } else if (res.status !== 401) {
        const data = await res.json();
        console.error(`refreshUserToken error [${res.status}]`, data);
      }
    } catch (e) {
      console.error("refreshUserToken error", e);
      return false;
    }
  }

  function clearLocalStorageData() {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("access_token_expire_at");
    localStorage.removeItem("refresh_token_expire_at");
    localStorage.removeItem("user");
    localStorage.removeItem("config");
  }

  function clearData() {
    user.value = null;
    access_token.value = null;
    access_token_expire_at.value = null;
    refresh_token.value = null;
    refresh_token_expire_at.value = null;
  }

  async function userLogin(username: string, password: string) {
    if (userRequest.value) {
      return false;
    }
    if (!username || !password) {
      userApiErrorMessage.value = "Введите логин и пароль";
      return false;
    }
    userApiErrorMessage.value = null;
    userMessage.value = null;
    userRequest.value = true;

    try {
      const res = await requestRaw("POST", `${API_AUTH_URL}/api/auth/login`, {
        headers: {
          Accept: "application/json",
        },
        body: { email: username, password },
      });

      if (res.status === 200) {
        const data = await res.json();
        updateTokenData(data);
        return true;
      } else if (res.status !== 401) {
        const data = await res.json();
        console.error(`userLogin error [${res.status}]`, data);
      }
      userApiErrorMessage.value = i18n.t("t-auth.wrong-login-password");
      return false;
    } catch (e) {
      console.error("refreshUserToken error", e);
      userApiErrorMessage.value = i18n.t("t-auth.wrong-login-password");
      return false;
    } finally {
      userRequest.value = false;
    }
  }

  async function userRecoveryPassword({
    code,
    password,
    password_confirmation,
  }: {
    code: string;
    password: string | null;
    password_confirmation: string | null;
  }) {
    if (userRequest.value) {
      return false;
    }
    if (!code) {
      userApiErrorMessage.value = "Пользователь не найден или ключ устарел";
      return false;
    }
    userApiErrorMessage.value = null;
    userRequest.value = true;

    try {
      const res = await requestRaw(
        "POST",
        `${API_AUTH_URL}/api/auth/recovery`,
        {
          headers: {
            Accept: "application/json",
          },
          body: { recovery_hash: code, password, password_confirmation },
        }
      );

      if (res.status === 200) {
        userMessage.value = "Пароль успешно обновлен. Войдите с новым паролем";
        return true;
      } else if (res.status !== 401) {
        const data = await res.json();
        if (res.status === 422) {
          userApiErrorMessage.value = data.message;
        } else {
          console.error(`userRecovery error [${res.status}]`, data);
        }
      }
      return false;
    } catch (e) {
      console.error("userRecovery error", e);
      return false;
    } finally {
      userRequest.value = false;
    }
  }

  async function userLogout() {
    if (userRequest.value) {
      return false;
    }
    userRequest.value = true;

    try {
      const res = await requestRaw(
        "POST",
        `${API_AUTH_URL}/api/auth/recovery`,
        {
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (res.status === 204) {
        clearLocalStorageData();
        clearData();
        return true;
      } else if (res.status !== 401) {
        const data = await res.json();
        console.error(`userLogout error [${res.status}]`, data);
      }
      return false;
    } catch (e) {
      console.error("userLogout error", e);
      return false;
    } finally {
      userRequest.value = false;
    }
  }

  function $reset() {
    clearData();
    clearLocalStorageData();
    userMessage.value = null;
  }

  return {
    access_token,
    refresh_token,
    userApiErrorMessage,
    userMessage,
    userRequest,

    accessIsExpired,
    refreshIsExpired,
    isAuthorised,

    user,
    userName,
    userRole,
    userDepartment,

    initStore,
    userLogin,
    userLogout,
    userRecoveryPassword,

    $reset,
  };
});
